<template>
  <NForm requireMarkPlacement="right" :show-feedback="false" size="medium">
    <NGrid :cols="2" :x-gap="16" :y-gap="16">
      <NFormItemGi :label="labels.tags" :span="2">
        <div class="flex flex-col gap-2 w-full">
          <CTagsPlaceholder v-if="showKeepAsIsPlaceholder" @select="handleTagsAction" />
          <CTags
            v-if="showTagsComponent"
            v-model:value="form.tags"
            data-field="tags"
            :suggested-tags-fetcher="(search) => fetchSuggestedInstanceTags(search, { type: ['files'] })"
            @blur="handleBlur('tags')"
          />
        </div>
      </NFormItemGi>
      <NFormItemGi v-if="showPermissionSettings" :label="labels.sharing">
        <CInputPlaceholder
          v-if="showKeepAsIsPlaceholder && !isEditingFieldOrNonEmpty('permissions')"
          @click="enableEditing('permissions', FieldTypes.SELECT)"
        />
        <CSelect
          v-else
          bordered
          :clearable="showKeepAsIsPlaceholder"
          data-field="permissions"
          :filterable="showKeepAsIsPlaceholder"
          :on-clear="
            async () => {
              await nextTick()
              handleBlur('permissions')
            }
          "
          :on-update:value="onChangeSharing"
          :options="sharingOptions"
          :value="sharingValueAsText"
          @blur="handleBlur('permissions')"
        />
      </NFormItemGi>
      <NFormItemGi v-if="showAccessTypeSettings" :label="labels.accessType">
        <CInputPlaceholder
          v-if="showKeepAsIsPlaceholder && !isEditingFieldOrNonEmpty('accessType')"
          @click="enableEditing('accessType', FieldTypes.SELECT)"
        />
        <CSelect
          v-else
          bordered
          :clearable="showKeepAsIsPlaceholder"
          data-field="accessType"
          :disabled="appName === 'impact'"
          :filterable="showKeepAsIsPlaceholder"
          :on-clear="
            async () => {
              await nextTick()
              handleBlur('accessType')
            }
          "
          :on-update:value="onChangeAccessType"
          :options="accessOptions"
          :value="form.accessType"
          @blur="handleBlur('accessType')"
        />
      </NFormItemGi>
      <NFormItemGi v-if="showExpirationSettings" :span="2">
        <template #label>
          <span>
            {{ t('canvasUI.browserApp.uploadFilesModal.expiresAt.label') }}
            <span v-if="isFileExpirationMandatory" class="color-error">*</span>
          </span>
        </template>
        <CInputPlaceholder
          v-if="showKeepAsIsPlaceholder && !isEditingFieldOrNonEmpty('expiresAtOption')"
          @click="enableEditing('expiresAtOption', FieldTypes.SELECT)"
        />
        <div v-else class="flex gap-4 w-full">
          <CSelect
            bordered
            class="w-50"
            :clearable="showKeepAsIsPlaceholder"
            data-field="expiresAtOption"
            :filterable="showKeepAsIsPlaceholder"
            :on-clear="
              async () => {
                await nextTick()
                handleBlur('expiresAtOption')
              }
            "
            :on-update:value="onChangeExpiresAtOption"
            :options="expiresAtOptions"
            :value="form.expiresAtOption"
            @blur="handleBlur('expiresAtOption')"
          />
          <NDatePicker
            :actions="datePickerActions"
            class="w-50"
            :clearable="!isFileExpirationMandatory"
            data-field="expiresAtDateTime"
            :disabled="form.expiresAtOption !== FileExpiration.At"
            :on-clear="
              async () => {
                await nextTick()
                handleBlur('expiresAtDateTime')
              }
            "
            :on-update:value="onChangeExpiresAtDateTime"
            :shortcuts="datePickerShortcuts"
            type="datetime"
            :value="form.expiresAtDateTime"
            @blur="handleBlur('expiresAtDateTime')"
          />
        </div>
      </NFormItemGi>
      <NFormItemGi
        v-if="showPermissionSettings && launchDarkly.enable_file_divisible_permission"
        :label="labels.divisible"
      >
        <CInputPlaceholder
          v-if="showKeepAsIsPlaceholder && !isEditingFieldOrNonEmpty('divisiblePermission')"
          @click="enableEditing('divisiblePermission', FieldTypes.SELECT)"
        />
        <CSwitch
          v-else
          :checked="divisibleValueAsBoolean"
          class="mx-1"
          data-field="divisiblePermission"
          @blur="handleBlur('divisiblePermission')"
          @update:checked="onChangeDivisible"
        />
      </NFormItemGi>
    </NGrid>
  </NForm>
</template>

<script setup lang="ts">
import { NForm, NFormItemGi, NGrid, NDatePicker } from 'naive-ui'
import { computed, ref, nextTick, inject, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'

import CInputPlaceholder from '@lib/components/CInputPlaceholder/CInputPlaceholder.vue'
import CSelect from '@lib/components/CSelect/CSelect.vue'
import CSwitch from '@lib/components/CSwitch/CSwitch.vue'
import CTags from '@lib/components/CTags/CTags.vue'
import CTagsPlaceholder from '@lib/components/CTagsPlaceholder/CTagsPlaceholder.vue'

import useSuggestedTags from '@lib/composables/suggestedTags.use'
import { dayjs } from '@lib/util/date'
import { AccessTypeEnum, MetadataTemplateFieldTypeEnum as FieldTypes } from '@openapi'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'

type SharingOption = 'allowed' | 'disabled'

enum FileExpiration {
  Never = 'never',
  At = 'at',
  Expired = 'expired',
}

export interface Form {
  permissions?: {
    can_share?: boolean
    can_be_divided?: boolean
  }
  tags?: string[]
  tagUpdateOperation?: 'add' | 'remove' | 'replace' | 'clear'
  expiresAtDateTime?: number | null
  expiresAtOption?: FileExpiration
  accessType?: AccessTypeEnum
  [key: string]: any
}

const props = withDefaults(
  defineProps<{
    modelValue: Form
    appName?: string
    showKeepAsIsPlaceholder?: boolean
    showExpirationSettings?: boolean
    showPermissionSettings?: boolean
    showAccessTypeSettings?: boolean
  }>(),
  {
    showExpirationSettings: true,
    showPermissionSettings: true,
    showAccessTypeSettings: true,
  },
)

const emit = defineEmits<{
  'update:modelValue': [form: Form]
}>()

const form = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
})

const { t } = useI18n()
const { fetchSuggestedInstanceTags } = useSuggestedTags()

const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const editingField = ref<string | null>(null)
const showTagsComponent = ref(!props.showKeepAsIsPlaceholder)

// Track the initial expiration option to keep "Never" available if file originally had it
const initialExpiresAtOption = ref(props.modelValue.expiresAtOption)

const labels: Record<string, string> = {
  sharing: t('canvasUI.browserApp.uploadFilesModal.sharing.label'),
  divisible: t('canvasUI.browserApp.uploadFilesModal.divisible.label'),
  tags: t('canvasUI.browserApp.uploadFilesModal.tags.label'),
  expiresAtDateTime: t('canvasUI.browserApp.uploadFilesModal.expiresAt.label'),
  accessType: t('canvasUI.browserApp.uploadFilesModal.access.label'),
}

const accessOptions = [
  { value: AccessTypeEnum.RESTRICTED, label: t('canvasUI.browserApp.uploadFilesModal.access.options.restricted') },
  { value: AccessTypeEnum.PUBLIC, label: t('canvasUI.browserApp.uploadFilesModal.access.options.public') },
]

const datePickerShortcuts = {
  [t('canvasUI.browserApp.uploadFilesModal.expiresAt.today')]: dayjs().endOf('day').valueOf(),
}

const isFileExpirationMandatory = computed(() => pitcherSettings.value?.is_file_expiration_mandatory)

const expiresAtOptions = computed(() => {
  const allOptions = [
    { value: FileExpiration.Never, label: t('canvasUI.browserApp.uploadFilesModal.expiresAt.options.never') },
    { value: FileExpiration.At, label: t('canvasUI.browserApp.uploadFilesModal.expiresAt.options.at') },
    { value: FileExpiration.Expired, label: t('canvasUI.browserApp.uploadFilesModal.expiresAt.options.expired') },
  ]

  if (isFileExpirationMandatory.value) {
    // Keep "never" option visible for files that originally had it set
    if (
      initialExpiresAtOption.value === FileExpiration.Never ||
      initialExpiresAtOption.value === null ||
      initialExpiresAtOption.value === undefined
    ) {
      return allOptions
    }
    return allOptions.filter((option) => option.value !== FileExpiration.Never)
  }

  return allOptions
})

const datePickerActions = computed<('clear' | 'confirm' | 'now')[]>(() =>
  isFileExpirationMandatory.value ? ['confirm'] : ['clear', 'confirm'],
)

async function updateForm(updates: Partial<Form>) {
  emit('update:modelValue', { ...form.value, ...updates })
}

function onChangeSharing(value: SharingOption | null) {
  updateForm({
    permissions: {
      ...form.value.permissions,
      can_share: value === null ? undefined : value === 'allowed',
    },
  })
}

function onChangeDivisible(value: boolean) {
  updateForm({
    permissions: {
      ...form.value.permissions,
      can_be_divided: value,
    },
  })
}

function onChangeAccessType(value: AccessTypeEnum | null) {
  updateForm({ accessType: value ?? undefined })
}

function onChangeExpiresAtOption(option: FileExpiration | undefined) {
  const updates: Partial<Form> = { expiresAtOption: option }

  if (option === FileExpiration.Never) {
    updates.expiresAtDateTime = null
  } else if (option === FileExpiration.Expired) {
    updates.expiresAtDateTime = dayjs().subtract(1, 'day').valueOf()
  } else if (option === FileExpiration.At) {
    if (!form.value.expiresAtDateTime || form.value.expiresAtDateTime < dayjs().valueOf()) {
      updates.expiresAtDateTime = dayjs().add(7, 'day').endOf('day').valueOf()
    }
  } else {
    // Handle undefined/cleared option
    if (isFileExpirationMandatory.value) {
      // Prevent clearing when expiration is mandatory - restore default value
      updates.expiresAtDateTime = dayjs().add(7, 'day').endOf('day').valueOf()
      updates.expiresAtOption = FileExpiration.At
    } else {
      updates.expiresAtDateTime = null
    }
  }

  updateForm(updates)
}

function onChangeExpiresAtDateTime(value: number | null) {
  const updates: Partial<Form> = { expiresAtDateTime: value }

  if (value === null) {
    if (isFileExpirationMandatory.value) {
      // Prevent clearing when expiration is mandatory - restore default value
      updates.expiresAtDateTime = dayjs().add(7, 'day').endOf('day').valueOf()
      updates.expiresAtOption = FileExpiration.At
    } else {
      updates.expiresAtOption = FileExpiration.Never
    }
  } else if (value < dayjs().valueOf()) {
    updates.expiresAtOption = FileExpiration.Expired
  } else {
    updates.expiresAtOption = FileExpiration.At
  }

  updateForm(updates)
}

const sharingOptions = [
  { value: 'allowed', label: t('canvasUI.files.sharing.options.allowed') },
  { value: 'disabled', label: t('canvasUI.files.sharing.options.disabled') },
]

const sharingValueAsText = computed({
  get: () => {
    if (form.value.permissions?.can_share === undefined) return null
    return form.value.permissions.can_share ? 'allowed' : 'disabled'
  },
  set: (value: SharingOption | null) => {
    if (value === null) {
      if (form.value.permissions) {
        form.value.permissions.can_share = undefined
      }
    } else {
      if (!form.value.permissions) {
        form.value.permissions = {}
      }
      form.value.permissions.can_share = value === 'allowed'
    }
  },
})

const divisibleValueAsBoolean = computed({
  get: () => {
    // Default to true when undefined for backwards compatibility
    return form.value.permissions?.can_be_divided ?? true
  },
  set: (value: boolean) => {
    if (!form.value.permissions) {
      form.value.permissions = {}
    }
    form.value.permissions.can_be_divided = value
  },
})

function isEditingFieldOrNonEmpty(fieldName: string) {
  if (fieldName === 'permissions') {
    return form.value.permissions?.can_share !== undefined || editingField.value === fieldName
  } else if (fieldName === 'divisiblePermission') {
    return form.value.permissions?.can_be_divided !== undefined || editingField.value === fieldName
  } else if (fieldName === 'tags') {
    return editingField.value === fieldName || (Array.isArray(form.value.tags) && form.value.tags.length > 0)
  }
  return editingField.value === fieldName || !!form.value[fieldName]
}

async function handleBlur(fieldName: string) {
  await nextTick()

  if (fieldName === 'permissions' && form.value.permissions?.can_share === undefined) {
    editingField.value = null
    return
  }

  if (fieldName === 'divisiblePermission' && form.value.permissions?.can_be_divided === undefined) {
    editingField.value = null
    return
  }

  if (fieldName === 'tags') {
    if (!Array.isArray(form.value.tags) || form.value.tags.length === 0) {
      editingField.value = null
      form.value.tags = undefined
    }
    return
  }

  if (!form.value[fieldName]) {
    editingField.value = null
    form.value[fieldName] = undefined
  }
}

function enableEditing(fieldName: string, fieldType: FieldTypes) {
  editingField.value = fieldName

  if (fieldName === 'permissions') {
    if (form.value.permissions?.can_share === undefined) {
      form.value.permissions = { ...form.value.permissions, can_share: true }
    }
  } else if (fieldName === 'divisiblePermission') {
    if (form.value.permissions?.can_be_divided === undefined) {
      form.value.permissions = { ...form.value.permissions, can_be_divided: true }
    }
  }

  nextTick(() => {
    const input = document.querySelector(`[data-field="${fieldName}"]`) as HTMLElement
    if (!input) return

    switch (fieldType) {
      case FieldTypes.SELECT:
      case FieldTypes.MULTI_SELECT: {
        const selectionInput = input.querySelector('.n-base-selection-input') as HTMLElement
        selectionInput?.click()
        break
      }
      case FieldTypes.DATE: {
        const dateInput = input.querySelector('input') as HTMLInputElement
        dateInput?.focus()
        break
      }
      default:
        console.warn(`Unhandled field type: ${fieldType}`)
    }
  })
}

function handleTagsAction(action: string) {
  form.value.tags = []
  switch (action) {
    case 'keep':
      showTagsComponent.value = false
      form.value.tagUpdateOperation = undefined
      break
    case 'add':
      showTagsComponent.value = true
      enableEditing('tags', FieldTypes.MULTI_SELECT)
      form.value.tagUpdateOperation = 'add'
      break
    case 'remove':
      showTagsComponent.value = true
      enableEditing('tags', FieldTypes.MULTI_SELECT)
      form.value.tagUpdateOperation = 'remove'
      break
    case 'replace':
      showTagsComponent.value = true
      enableEditing('tags', FieldTypes.MULTI_SELECT)
      form.value.tagUpdateOperation = 'replace'
      break
    case 'clear':
      showTagsComponent.value = false
      form.value.tagUpdateOperation = 'clear'
      break
  }
}
</script>
